// src/encounter/lib/to-imaging-order.ts
// Map a picked imaging CodeSearchResult (from the radiology search) + a free-text note → a DS
// PlanOrder attached to a diagnosis on the AssessmentPlanCanvas, plus the structured side-map
// payload (the canvas PlanOrder is lossy {id,type,label}; codes + note live in OrderPayloadMap,
// keyed by orderId). The caller supplies the orderId (unique per attachment — the same study can
// be ordered against more than one Dx, so the id is NOT derived from the code, which would collide).
// No sub-test panel for imaging (unlike lab): just the code + note. label = the display string.
import type { CodeSearchResult, PlanOrder } from '@scripsteam/scrips-signal-ds';
import type { CodedOrderPayload, OrderCoding } from './order-payload';

export interface ImagingOrderResult {
  planOrder: PlanOrder;
  payload: Extract<CodedOrderPayload, { kind: 'imaging' }>;
}

export function toImagingOrder(
  result: CodeSearchResult,
  orderId: string,
  note?: string,
): ImagingOrderResult {
  const display = result.display.trim();
  // Coding falls back code→display so a free-text/custom hit (no `code`) still carries a value.
  const code: OrderCoding = {
    code: result.code?.trim() || display,
    displayName: display,
    ...(result.system?.trim() ? { system: result.system.trim() } : {}),
  };
  const trimmedNote = note?.trim();
  return {
    // dxIds empty — the host attaches the target Dx when it appends the order (v2 PROD-1422).
    planOrder: { id: orderId, type: 'imaging', label: display, dxIds: [] },
    payload: {
      kind: 'imaging',
      code,
      ...(trimmedNote ? { note: trimmedNote } : {}),
    },
  };
}
